// Seznam operativ seskupeny podle ISO tydnu (po-pa, vikendy ignorujeme).
// Kazdy tyden ma vlastni Card s 5 radky a tlacitky "Uzavrit tyden" + "PDF tydne".

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, FileDown, Loader2, Lock, Plus, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { usePermissions } from '@/lib/permissions';
import {
  DAY_LABELS_CS,
  formatWeekRangeCs,
  isoDate,
  isoDateOffset,
  isoWeekNumber,
  parseIsoDate,
  startOfIsoWeek,
} from '@/lib/dateWeek';
import type {
  CreateOperativaInput,
  KopieOperativaInput,
  OperativaDetail,
  OperativaListItem,
  UzavritTydenResponse,
} from '@/types/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { StavBadge } from '@/components/StavBadge';

type DialogState =
  | { mode: 'closed' }
  | { mode: 'create'; presetDatum?: string }
  | { mode: 'copy'; item: OperativaListItem }
  | { mode: 'delete'; item: OperativaListItem }
  | { mode: 'uzavritTyden'; monday: string };

/** Vraci pondeli (lokalni Date) pro defaultni filtr — zacatek minulych 4 tydnu. */
function defaultFilterFrom(): string {
  const thisMonday = startOfIsoWeek(new Date());
  const back4 = new Date(thisMonday);
  back4.setDate(back4.getDate() - 21); // 4 tydny nazpet (vc. tehoto = 3*7 dni)
  return isoDate(back4);
}

/** Vraci patek pristiho tydne. */
function defaultFilterTo(): string {
  const thisMonday = startOfIsoWeek(new Date());
  const nextFriday = new Date(thisMonday);
  nextFriday.setDate(nextFriday.getDate() + 7 + 4);
  return isoDate(nextFriday);
}

interface WeekGroup {
  /** Pondeli daneho tydne (lokalni Date). */
  monday: Date;
  /** ISO YYYY-MM-DD pondeli — klic do mapy. */
  mondayIso: string;
  isoYear: number;
  isoWeek: number;
  /** 5 dni Po-Pa, indexovano 0..4. */
  days: Array<{
    date: Date;
    iso: string;
    label: string; // "Pondělí"
    operativa: OperativaListItem | null;
  }>;
}

/**
 * Seskupi operativy podle ISO pondeli a dorovna kazdy tyden na 5 dni (po-pa).
 * Generuje vsechny tydny v rozsahu filtru, vc. prazdnych (aby uzivatel videl, ze
 * pro dany den nic neni naplanovano).
 */
function groupByWeek(items: OperativaListItem[], od: string, doDate: string): WeekGroup[] {
  const byDate = new Map<string, OperativaListItem>();
  for (const it of items) {
    byDate.set(it.datum, it);
  }

  const start = startOfIsoWeek(parseIsoDate(od));
  const end = parseIsoDate(doDate);

  const groups: WeekGroup[] = [];
  for (
    let monday = new Date(start);
    monday <= end;
    monday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7)
  ) {
    const { year, week } = isoWeekNumber(monday);
    const days = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(date.getDate() + i);
      const iso = isoDate(date);
      days.push({
        date,
        iso,
        label: DAY_LABELS_CS[i],
        operativa: byDate.get(iso) ?? null,
      });
    }
    groups.push({
      monday,
      mondayIso: isoDate(monday),
      isoYear: year,
      isoWeek: week,
      days,
    });
  }

  // Descending — nejnovejsi tyden nahore.
  groups.sort((a, b) => b.monday.getTime() - a.monday.getTime());
  return groups;
}

/** Format "25. 5." (den + mesic, bez roku). */
function formatShortDate(d: Date): string {
  return `${d.getDate()}. ${d.getMonth() + 1}.`;
}

export function OperativyListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const perms = usePermissions();

  // Default rozsah: 4 tydny nazpet az patek pristiho tydne. Pri renderu zustanou
  // vsechny tydny v rozsahu zobrazene jako Card (i prazdne — viz groupByWeek).
  const [od, setOd] = useState<string>(() => defaultFilterFrom());
  const [doDate, setDoDate] = useState<string>(() => defaultFilterTo());
  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' });

  const queryKey = useMemo(() => ['operativy', od, doDate] as const, [od, doDate]);

  const listQuery = useQuery<OperativaListItem[]>({
    queryKey,
    queryFn: () =>
      api<OperativaListItem[]>(
        `/operativy?od=${encodeURIComponent(od)}&doDate=${encodeURIComponent(doDate)}`,
      ),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateOperativaInput) =>
      api<OperativaDetail>('/operativy', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Operativa vytvořena' });
      setDialog({ mode: 'closed' });
      navigate(`/operativy/${created.id}`);
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) {
        toast({
          title: 'Operativa pro tento den už existuje',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Vytvoření selhalo',
          description: err.message,
          variant: 'destructive',
        });
      }
    },
  });

  const copyMutation = useMutation({
    mutationFn: ({ sourceId, cilDatum }: { sourceId: string; cilDatum: string }) =>
      api<OperativaDetail>(`/operativy/${sourceId}/kopie`, {
        method: 'POST',
        body: JSON.stringify({ cilDatum } satisfies KopieOperativaInput),
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Operativa zkopírována' });
      setDialog({ mode: 'closed' });
      navigate(`/operativy/${created.id}`);
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) {
        toast({
          title: 'Operativa pro cílový den už existuje',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Kopírování selhalo',
          description: err.message,
          variant: 'destructive',
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api<void>(`/operativy/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      toast({ title: 'Operativa smazána' });
      setDialog({ mode: 'closed' });
    },
    onError: (err: Error) => {
      if (err instanceof ApiError && err.status === 409) {
        toast({
          title: 'Operativu nelze smazat',
          description: 'V jejím aktuálním stavu už mazání není povoleno.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Smazání selhalo',
          description: err.message,
          variant: 'destructive',
        });
      }
    },
  });

  const uzavritTydenMutation = useMutation({
    mutationFn: (tydenOd: string) =>
      api<UzavritTydenResponse>(
        `/operativy/uzavrit-tyden?tydenOd=${encodeURIComponent(tydenOd)}`,
        { method: 'POST' },
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['operativy'] });
      setDialog({ mode: 'closed' });
      const parts: string[] = [];
      parts.push(`Uzavřeno ${res.uzavreno.length} dní`);
      if (res.preskoceno.length > 0) parts.push(`přeskočeno ${res.preskoceno.length}`);
      if (res.chyby.length > 0) parts.push(`chyb ${res.chyby.length}`);
      toast({
        title: 'Uzavření týdne dokončeno',
        description: parts.join(', '),
        variant: res.chyby.length > 0 ? 'destructive' : 'default',
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Uzavření týdne selhalo',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const items = listQuery.data ?? [];
  const weekGroups = useMemo(() => groupByWeek(items, od, doDate), [items, od, doDate]);

  const setQuickThisWeek = () => {
    const mon = startOfIsoWeek(new Date());
    const fri = new Date(mon);
    fri.setDate(fri.getDate() + 4);
    setOd(isoDate(mon));
    setDoDate(isoDate(fri));
  };

  const setQuickLastWeek = () => {
    const mon = startOfIsoWeek(new Date());
    mon.setDate(mon.getDate() - 7);
    const fri = new Date(mon);
    fri.setDate(fri.getDate() + 4);
    setOd(isoDate(mon));
    setDoDate(isoDate(fri));
  };

  const setQuickDefault = () => {
    setOd(defaultFilterFrom());
    setDoDate(defaultFilterTo());
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Operativy</h2>
          <p className="text-sm text-muted-foreground">Denní plán montážních prací</p>
        </div>
        {perms.canVytvoritOperativu && (
          <Button onClick={() => setDialog({ mode: 'create' })}>
            <Plus className="mr-2 h-4 w-4" />
            Nová operativa na den…
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtr</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="od">Od</Label>
              <Input
                id="od"
                type="date"
                value={od}
                onChange={(e) => setOd(e.target.value)}
                className="w-44"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="do">Do</Label>
              <Input
                id="do"
                type="date"
                value={doDate}
                onChange={(e) => setDoDate(e.target.value)}
                className="w-44"
              />
            </div>
            <Button variant="outline" onClick={setQuickThisWeek}>
              Tento týden
            </Button>
            <Button variant="outline" onClick={setQuickLastWeek}>
              Minulý týden
            </Button>
            <Button variant="outline" onClick={setQuickDefault}>
              4 týdny nazpět + 1 vpřed
            </Button>
          </div>
        </CardContent>
      </Card>

      {listQuery.isLoading ? (
        <Card>
          <CardContent>
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      ) : listQuery.isError ? (
        <Card>
          <CardContent>
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Načtení selhalo: {(listQuery.error as Error).message}
            </div>
          </CardContent>
        </Card>
      ) : weekGroups.length === 0 ? (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-muted-foreground">
              Pro zvolený rozsah nejsou žádné týdny.
            </p>
          </CardContent>
        </Card>
      ) : (
        weekGroups.map((wg) => (
          <WeekCard
            key={wg.mondayIso}
            group={wg}
            perms={perms}
            onCreate={(datum) => setDialog({ mode: 'create', presetDatum: datum })}
            onCopy={(item) => setDialog({ mode: 'copy', item })}
            onDelete={(item) => setDialog({ mode: 'delete', item })}
            onUzavritTyden={() => setDialog({ mode: 'uzavritTyden', monday: wg.mondayIso })}
          />
        ))
      )}

      <CreateOperativaDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onSubmit={(datum) => createMutation.mutate({ datum })}
        submitting={createMutation.isPending}
      />

      <CopyOperativaDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onSubmit={(cilDatum) => {
          if (dialog.mode === 'copy') {
            copyMutation.mutate({ sourceId: dialog.item.id, cilDatum });
          }
        }}
        submitting={copyMutation.isPending}
      />

      <DeleteOperativaDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onConfirm={(id) => deleteMutation.mutate(id)}
        submitting={deleteMutation.isPending}
      />

      <UzavritTydenDialog
        state={dialog}
        onClose={() => setDialog({ mode: 'closed' })}
        onConfirm={(monday) => uzavritTydenMutation.mutate(monday)}
        submitting={uzavritTydenMutation.isPending}
      />
    </div>
  );
}

interface WeekCardProps {
  group: WeekGroup;
  perms: ReturnType<typeof usePermissions>;
  onCreate: (datum: string) => void;
  onCopy: (item: OperativaListItem) => void;
  onDelete: (item: OperativaListItem) => void;
  onUzavritTyden: () => void;
}

function WeekCard({ group, perms, onCreate, onCopy, onDelete, onUzavritTyden }: WeekCardProps) {
  const todayIso = isoDate(new Date());

  const handleOpenPdf = () => {
    // tydenni-souhrn pouziva GET; otevreme v novem tabu, browser stahne nebo zobrazi PDF.
    window.open(
      `/api/exporty/tydenni-souhrn?tydenOd=${encodeURIComponent(group.mondayIso)}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  // Tlacitko "Uzavrit tyden" zobrazujeme jen pro role VEDOUCI / ADMIN — backend
  // stejne validuje per-operativa, takze to je jen UI gating.
  const canUzavritTyden = perms.isVedouci || perms.isAdmin;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>
            {group.isoWeek}. týden {group.isoYear}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              · {formatWeekRangeCs(group.monday)}
            </span>
          </CardTitle>
          <div className="flex gap-2">
            {canUzavritTyden && (
              <Button variant="outline" size="sm" onClick={onUzavritTyden}>
                <Lock className="mr-2 h-4 w-4" />
                Uzavřít týden
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleOpenPdf}>
              <FileDown className="mr-2 h-4 w-4" />
              PDF týdne
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="divide-y rounded-md border">
          {group.days.map((day) => {
            const isToday = day.iso === todayIso;
            const op = day.operativa;
            return (
              <div
                key={day.iso}
                className={`grid grid-cols-12 items-center gap-3 px-3 py-2 text-sm ${
                  isToday ? 'bg-accent/40' : ''
                }`}
              >
                <div className="col-span-3 md:col-span-2">
                  <div className={`font-medium ${isToday ? 'text-primary' : ''}`}>
                    {day.label}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatShortDate(day.date)}
                  </div>
                </div>
                {op ? (
                  <>
                    <div className="col-span-3 md:col-span-2">
                      <StavBadge stav={op.stav} type="operativa" />
                    </div>
                    <div className="col-span-3 md:col-span-4 text-muted-foreground">
                      {op.vytvorilJmeno}
                    </div>
                    <div className="col-span-1 md:col-span-1 text-right tabular-nums">
                      {op.pocetPolozek}
                    </div>
                    <div className="col-span-2 md:col-span-3 flex justify-end gap-1">
                      <Button variant="ghost" size="sm" asChild title="Otevřít detail">
                        <Link to={`/operativy/${op.id}`}>
                          <Eye className="mr-1 h-4 w-4" />
                          Otevřít
                        </Link>
                      </Button>
                      {perms.canKopirovatOperativu && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCopy(op)}
                          title="Kopírovat na jiný den"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      )}
                      {perms.canSmazatOperativu(op.stav) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(op)}
                          title="Smazat"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-7 md:col-span-7 text-muted-foreground">—</div>
                    <div className="col-span-2 md:col-span-3 flex justify-end">
                      {perms.canVytvoritOperativu && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCreate(day.iso)}
                          title="Vytvořit operativu na tento den"
                        >
                          <Plus className="mr-1 h-4 w-4" />
                          Nová
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

interface CreateProps {
  state: DialogState;
  onClose: () => void;
  onSubmit: (datum: string) => void;
  submitting: boolean;
}

function CreateOperativaDialog({ state, onClose, onSubmit, submitting }: CreateProps) {
  const open = state.mode === 'create';
  const preset = state.mode === 'create' ? state.presetDatum : undefined;
  const [datum, setDatum] = useState<string>(() => preset ?? isoDateOffset(1));

  useEffect(() => {
    if (open) setDatum(preset ?? isoDateOffset(1));
  }, [open, preset]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!datum) return;
    onSubmit(datum);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nová operativa</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-datum">Datum operativy</Label>
            <Input
              id="new-datum"
              type="date"
              required
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Vznikne prázdná operativa, do které následně přidáte položky.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Zrušit
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Vytvořit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface CopyProps {
  state: DialogState;
  onClose: () => void;
  onSubmit: (cilDatum: string) => void;
  submitting: boolean;
}

function CopyOperativaDialog({ state, onClose, onSubmit, submitting }: CopyProps) {
  const open = state.mode === 'copy';
  const source = state.mode === 'copy' ? state.item : null;
  const [cilDatum, setCilDatum] = useState<string>(() => isoDateOffset(1));

  useEffect(() => {
    if (open) setCilDatum(isoDateOffset(1));
  }, [open]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!cilDatum) return;
    onSubmit(cilDatum);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Kopírovat operativu</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {source && (
            <p className="text-sm text-muted-foreground">
              Zdrojová operativa: <strong>{source.datum}</strong> ({source.pocetPolozek} položek).
              Položky se zkopírují s pořadím, stav položek se nastaví na „Naplánováno".
            </p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="cil-datum">Cílový den</Label>
            <Input
              id="cil-datum"
              type="date"
              required
              value={cilDatum}
              onChange={(e) => setCilDatum(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Zrušit
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Kopírovat'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DeleteProps {
  state: DialogState;
  onClose: () => void;
  onConfirm: (id: string) => void;
  submitting: boolean;
}

function DeleteOperativaDialog({ state, onClose, onConfirm, submitting }: DeleteProps) {
  const open = state.mode === 'delete';
  const item = state.mode === 'delete' ? state.item : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Smazat operativu?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Opravdu chcete smazat operativu na den <strong>{item?.datum ?? ''}</strong>?
          Smaže se i všechny její položky. Tato akce je nevratná.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zrušit
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={submitting}
            onClick={() => item && onConfirm(item.id)}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Smazat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface UzavritTydenProps {
  state: DialogState;
  onClose: () => void;
  onConfirm: (mondayIso: string) => void;
  submitting: boolean;
}

function UzavritTydenDialog({ state, onClose, onConfirm, submitting }: UzavritTydenProps) {
  const open = state.mode === 'uzavritTyden';
  const monday = state.mode === 'uzavritTyden' ? state.monday : null;
  const rangeLabel = monday ? formatWeekRangeCs(parseIsoDate(monday)) : '';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Uzavřít týden?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Uzavřou se všechny operativy v rozsahu <strong>{rangeLabel}</strong>, které jsou
          ve stavu „Schváleno" nebo „V realizaci". Operativy v jiných stavech budou
          přeskočeny.
        </p>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Zrušit
          </Button>
          <Button
            type="button"
            disabled={submitting}
            onClick={() => monday && onConfirm(monday)}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Uzavřít týden'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
